import { useTranslation } from 'react-i18next'
import { Button, Checkbox, Link, Spinner } from '@structural-climbing/ui'
import { SignatureCanvas, SignaturePadHandle } from '../../components/SignatureCanvas'

export type SignPanelStep = 'view' | 'sign'

/** One declaration the gym wrote for a document; the signer ticks each one. */
export interface DocumentStatement {
  id: string
  text: string
  required: boolean
}

/** Whether every required statement is among the ticked ids. */
export function requiredStatementsTicked(
  statements: DocumentStatement[] | undefined,
  ticked: string[],
): boolean {
  return (statements ?? []).every((s) => !s.required || ticked.includes(s.id))
}

interface Props {
  step: SignPanelStep
  docTitle: string
  docUrl: string | null
  docIsPdf: boolean
  docLoading: boolean
  error?: string | null
  /** Disables the actions and spins the sign button while a request is in flight. */
  busy?: boolean
  /** The final call to action, which differs per screen: Submit, Enter or Continue. */
  signLabel: string
  canvasEmpty: boolean
  /** What the signer ticks before signing. None means the document has none. */
  statements?: DocumentStatement[]
  /** Ids of the ticked statements. Starts empty: nothing is ever pre-ticked. */
  ticked?: string[]
  onTickedChange?: (ticked: string[]) => void
  /** The gym's privacy notice, opened in a new tab. Null when it has none. */
  privacyNoticeUrl?: string | null
  sigRef: React.Ref<SignaturePadHandle>
  onCanvasEnd: () => void
  onClear: () => void
  onProceed: () => void
  onBack: () => void
  onSign: () => void
}

/**
 * The document viewer and the signature pad, as one height-aware column.
 *
 * This screen existed three times — the join flow, the guest join flow and the
 * legal gate — as copy-pasted markup, which is how one layout bug shipped in
 * three places: the viewer was sized in `vh` (`h-[60vh]`, `min-h-[50vh]`), so on
 * a short window it refused to shrink and pushed "Proceed to Sign" off the
 * bottom. On mobile `vh` is the viewport with the URL bar collapsed, and this
 * app scrolls an inner div rather than the document, so that bar never
 * collapses and the overshoot is permanent.
 *
 * The fix is structural rather than a smaller number: the viewer is
 * `min-h-0 flex-1` and shrinks to whatever is left, while the footer is
 * `shrink-0` and cannot be pushed anywhere. A member on a cramped screen gets a
 * small PDF and a reachable button, with "Open in new tab" sitting right above
 * it for when the small PDF is not enough.
 *
 * Callers own their own header and card chrome, and all the state — this
 * renders, it does not fetch or submit.
 *
 * The sign step is also where the signer accepts the gym's statements one by
 * one. Three things there are deliberate and came from a gym's lawyer: every
 * box starts unticked and is ticked by the signer, the sign button stays
 * disabled until each required one is, and all of it is set in one size and
 * one colour, with nothing greyed out. Do not dim an optional statement or the
 * notice link to make the screen look tidier.
 */
export function DocumentSignPanel({
  step,
  docTitle,
  docUrl,
  docIsPdf,
  docLoading,
  error,
  busy = false,
  signLabel,
  canvasEmpty,
  statements = [],
  ticked = [],
  onTickedChange,
  privacyNoticeUrl,
  sigRef,
  onCanvasEnd,
  onClear,
  onProceed,
  onBack,
  onSign,
}: Props) {
  const { t } = useTranslation()
  const statementsDone = requiredStatementsTicked(statements, ticked)

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {step === 'view' ? (
        <>
          <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-divider bg-white">
            {docLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner size="lg" color="primary" />
              </div>
            ) : docUrl ? (
              docIsPdf ? (
                <iframe src={docUrl} className="h-full w-full border-none" title={docTitle} />
              ) : (
                <div className="h-full overflow-y-auto p-4">
                  <img src={docUrl} alt={docTitle} className="w-full object-contain" />
                </div>
              )
            ) : (
              <div className="flex h-full items-center justify-center px-4 text-center text-sm text-foreground/40">
                {t('legal.failedLoadDoc')}
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            {docIsPdf && docUrl && (
              <a
                href={docUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-2 text-center text-sm font-medium text-primary underline sm:py-3 sm:text-base"
              >
                {t('legal.openInNewTab')}
              </a>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button
              color="primary"
              isDisabled={docLoading || !docUrl}
              onClick={onProceed}
              className="w-full"
            >
              {t('legal.proceedToSign')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            {statements.length > 0 && (
              <div className="flex flex-col gap-3 pb-2">
                <p className="text-sm text-foreground">{t('legal.tickStatements')}</p>
                {statements.map((statement) => (
                  <Checkbox
                    key={statement.id}
                    isSelected={ticked.includes(statement.id)}
                    isDisabled={busy}
                    onValueChange={(selected) =>
                      onTickedChange?.(
                        selected
                          ? [...ticked, statement.id]
                          : ticked.filter((id) => id !== statement.id),
                      )
                    }
                    classNames={{ base: 'items-start', label: 'text-sm text-foreground' }}
                  >
                    {statement.required
                      ? statement.text
                      : t('legal.statementOptional', { text: statement.text })}
                  </Checkbox>
                ))}
              </div>
            )}
            {privacyNoticeUrl && (
              <Link
                href={privacyNoticeUrl}
                isExternal
                underline="always"
                className="self-start text-sm text-foreground"
              >
                {t('legal.privacyNoticeLink')}
              </Link>
            )}
            <p className="text-sm text-foreground">{t('legal.drawSignature')}</p>
            <div className="rounded-xl border-2 border-dashed border-divider bg-white">
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ className: 'w-full h-40 sm:h-56' }}
                penColor="black"
                backgroundColor="white"
                onEnd={onCanvasEnd}
              />
            </div>
            <Button
              variant="light"
              size="sm"
              isDisabled={busy}
              onClick={onClear}
              className="self-start"
            >
              {t('common.clear')}
            </Button>
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-3">
              <Button variant="bordered" isDisabled={busy} onClick={onBack} className="flex-1">
                {t('legal.backToDocument')}
              </Button>
              <Button
                color="primary"
                isDisabled={canvasEmpty || busy || !statementsDone}
                isLoading={busy}
                onClick={onSign}
                className="flex-1"
              >
                {signLabel}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
